import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Key, Eye, EyeOff, Save, RefreshCw, Shield, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCredentials = () => {
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [revealedFields, setRevealedFields] = useState({});
  const [editMode, setEditMode] = useState({});
  const [editValues, setEditValues] = useState({});

  const token = localStorage.getItem('mojo_admin_token');

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredentials(response.data.credentials);
    } catch (error) {
      toast.error('Failed to load credentials');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const revealCredential = async (category, service, field) => {
    const key = `${category}.${service}.${field}`;
    
    try {
      const response = await axios.post(
        `${API}/admin/credentials/reveal`,
        { category, service, field },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { category, service, field }
        }
      );
      
      setRevealedFields(prev => ({
        ...prev,
        [key]: response.data.value
      }));
      
      toast.success('Credential revealed');
    } catch (error) {
      toast.error('Failed to reveal credential');
    }
  };

  const toggleEdit = (category, service, field, currentValue) => {
    const key = `${category}.${service}.${field}`;
    
    if (editMode[key]) {
      // Cancel edit
      setEditMode(prev => ({ ...prev, [key]: false }));
      setEditValues(prev => ({ ...prev, [key]: undefined }));
    } else {
      // Start edit
      setEditMode(prev => ({ ...prev, [key]: true }));
      setEditValues(prev => ({ ...prev, [key]: currentValue }));
    }
  };

  const saveCredential = async (category, service, field) => {
    const key = `${category}.${service}.${field}`;
    const newValue = editValues[key];
    
    if (!newValue) {
      toast.error('Value cannot be empty');
      return;
    }
    
    setSaving(true);
    try {
      const updates = {
        [category]: {
          [service]: {
            [field]: newValue
          }
        }
      };
      
      await axios.put(
        `${API}/admin/credentials`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Credential updated successfully');
      setEditMode(prev => ({ ...prev, [key]: false }));
      setRevealedFields(prev => ({ ...prev, [key]: undefined }));
      fetchCredentials();
    } catch (error) {
      toast.error('Failed to update credential');
    } finally {
      setSaving(false);
    }
  };

  const CredentialField = ({ category, service, field, value, label, isPassword = true }) => {
    const key = `${category}.${service}.${field}`;
    const isRevealed = revealedFields[key] !== undefined;
    const isEditing = editMode[key];
    const isMasked = value === '***masked***' || (isPassword && !isRevealed);
    
    const displayValue = isEditing 
      ? editValues[key] 
      : (isRevealed ? revealedFields[key] : value);

    return (
      <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
        <Label className="text-sm font-medium text-gray-700">{label}</Label>
        <div className="flex gap-2">
          {isEditing ? (
            <Input
              type={isPassword && !isRevealed ? 'password' : 'text'}
              value={displayValue || ''}
              onChange={(e) => setEditValues(prev => ({ ...prev, [key]: e.target.value }))}
              className="flex-1 font-mono text-sm"
            />
          ) : (
            <Input
              type={isMasked ? 'password' : 'text'}
              value={displayValue || ''}
              readOnly
              className="flex-1 font-mono text-sm bg-white"
            />
          )}
          
          <div className="flex gap-1">
            {isMasked && !isEditing && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => revealCredential(category, service, field)}
                title="Reveal"
              >
                <Eye className="w-4 h-4" />
              </Button>
            )}
            
            {isEditing ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => saveCredential(category, service, field)}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Save className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => toggleEdit(category, service, field, value)}
                >
                  ✕
                </Button>
              </>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => toggleEdit(category, service, field, value)}
                title="Edit"
              >
                ✏️
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ServiceCard = ({ category, service, data }) => (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg capitalize">{service.replace(/_/g, ' ')}</CardTitle>
          <div className="flex items-center gap-2">
            {data.enabled !== undefined && (
              <span className={`px-2 py-1 text-xs rounded ${data.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {data.enabled ? 'Enabled' : 'Disabled'}
              </span>
            )}
          </div>
        </div>
        {data.note && (
          <CardDescription className="text-sm text-yellow-700 bg-yellow-50 p-2 rounded mt-2">
            ⚠️ {data.note}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {Object.entries(data).map(([field, value]) => {
          if (field === 'enabled' || field === 'note' || field === 'mode') return null;
          
          const isPassword = field.includes('key') || field.includes('password') || field.includes('secret') || field.includes('private');
          
          return (
            <CredentialField
              key={field}
              category={category}
              service={service}
              field={field}
              value={value}
              label={field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              isPassword={isPassword}
            />
          );
        })}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin mx-auto text-[#F2B10C] mb-4" />
          <p className="text-gray-600">Loading credentials...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8 text-[#1B0C34]" />
          <h1 className="text-3xl font-bold text-[#1B0C34]">Credentials Management</h1>
        </div>
        <p className="text-gray-600">Securely manage all API keys and service credentials</p>
        
        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-yellow-800 font-medium">Security Notice</p>
              <p className="text-xs text-yellow-700 mt-1">
                All credential changes are logged. Some services may require restart to apply changes.
                Never share credentials with unauthorized personnel.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Credentials by Category */}
      {credentials && Object.entries(credentials).map(([category, services]) => {
        if (category === 'config_type' || category === 'updated_at' || category === 'updated_by') return null;
        
        return (
          <div key={category} className="mb-8">
            <h2 className="text-2xl font-bold text-[#1B0C34] mb-4 capitalize flex items-center gap-2">
              <Key className="w-6 h-6 text-[#F2B10C]" />
              {category.replace(/_/g, ' ')}
            </h2>
            
            {Object.entries(services).map(([service, data]) => (
              <ServiceCard
                key={service}
                category={category}
                service={service}
                data={data}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default AdminCredentials;
